import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { COMPANY, MEDIA, DIRECTORS, COMMITTEES } from "@/lib/assets";
import { Compass, ShieldCheck, HandHeart, Users, Briefcase } from "lucide-react";

const VALUES = [
  { icon: Compass, title: "Reliability", body: "Consistent grades, consistent lead times — because your production line does not wait." },
  { icon: ShieldCheck, title: "Trust", body: "Direct dealership with the mills we represent — no middlemen, no surprises." },
  { icon: HandHeart, title: "Service", body: "A hands-on trade desk that treats every enquiry as the start of a long relationship." },
];

const useHashScroll = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [hash]);
};

export default function About() {
  useHashScroll();
  return (
    <div className="bg-background" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <header className="pt-40 md:pt-52 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ About Us</p>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.9] max-w-4xl">
              The story behind<br /> <span className="italic">Paptech.</span>
            </h1>
            <p className="text-muted-foreground max-w-xl mt-8 text-lg leading-relaxed">
              A young company built on a simple idea — that ordering paper and paperboard should be as reliable as the mills that produce it.
            </p>
          </FadeUp>
        </div>
      </header>

      {/* About Company */}
      <section id="company" className="py-24 md:py-28 scroll-mt-24" data-testid="section-company">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <FadeUp className="lg:col-span-7">
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ About Company</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">A supply chain, engineered.</h2>
            <div className="mt-8 space-y-5 text-foreground/85 leading-relaxed max-w-2xl">
              <p>
                {COMPANY.name} is a paper and paperboard supply-chain company built on a simple idea — that ordering
                paper should be as reliable as the mills that produce it. We source, stock, sheet-cut and deliver
                FBB / SBS, C2S coated and Recycled Grey / White Back boards to converters and brand owners across India.
              </p>
              <p>
                As authorised dealers for leading domestic and international mills — APP (China & Indonesia),
                Three M Paper Boards, Tirthak Paper Mill, N R Agarwal Industries and Tulsi Paper Mill — we combine
                direct-from-mill sourcing with in-house quick sheet-cutting capacity of approximately 4,000 tonnes
                per month across our Bhiwandi (Maharashtra) and Ahmedabad (Gujarat) centres.
              </p>
              <p>
                Five stocking warehouses across the west zone — Aamne and Gupta in Bhiwandi; Shyam Estate, Pirana 1
                and Pirana 2 in Ahmedabad — keep inventory close to manufacturing hubs, so our customers get the
                right grade, cut to size, right on schedule. Simply put: paper ordering, now simplified.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden border border-border">
              <img src={MEDIA.warehouseRolls} alt="Paptech warehouse" className="w-full h-[520px] object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Values */}
      <section className="bg-muted border-y border-border py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ What we stand for</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] max-w-3xl">Three principles, every consignment.</h2>
          </FadeUp>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-border border border-border">
            {VALUES.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.08}>
                <div className="bg-background p-8 md:p-10 h-full min-h-[260px]" data-testid={`value-${i}`}>
                  <v.icon size={26} className="text-primary" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl mt-8">{v.title}</h3>
                  <p className="text-muted-foreground mt-3 leading-relaxed">{v.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Board of Directors */}
      <section id="board" className="py-20 md:py-24 scroll-mt-24" data-testid="section-board">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-4">
              <Users size={22} className="text-primary" strokeWidth={1.5} />
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary">/ Board of Directors</p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] max-w-3xl">Stewards of the company.</h2>
            <p className="text-muted-foreground max-w-2xl mt-6 leading-relaxed">
              Composition, biographies (including DIN, designation and areas of expertise) of the Board of Directors
              will be published here upon formal appointment and disclosure in line with the Companies Act, 2013.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border" data-testid="board-list">
              {DIRECTORS.map((d, i) => (
                <div key={d.name} className="bg-card p-6 md:p-7 h-full flex flex-col" data-testid={`director-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display text-xl leading-snug">{d.name}</h3>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary mt-1.5">{d.role}</p>
                    </div>
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-border text-muted-foreground whitespace-nowrap">{d.tag}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2">{d.profile}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Board Committees */}
      <section id="committees" className="bg-foreground text-background py-20 md:py-24 scroll-mt-24" data-testid="section-committees">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-4">
              <Briefcase size={22} className="text-primary" strokeWidth={1.5} />
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary">/ Board Committees</p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] max-w-3xl">Governance in structure.</h2>
            <p className="text-background/70 max-w-2xl mt-6 leading-relaxed">
              The Board discharges its responsibilities through statutory and non-statutory committees. The composition
              of each committee, including chairperson and members, will be published upon constitution.
            </p>
          </FadeUp>
          <div className="mt-12 grid md:grid-cols-2 gap-px bg-background/15">
            {COMMITTEES.map((c, i) => (
              <FadeUp key={c.name} delay={i * 0.06}>
                <div className="bg-foreground p-6 md:p-8 h-full flex flex-col" data-testid={`committee-${i}`}>
                  <span className="font-mono2 text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl mt-3 leading-snug">{c.name}</h3>
                  <ul className="mt-5 space-y-3">
                    {c.members.map((m) => (
                      <li key={m.name} className="flex items-start justify-between gap-4 border-t border-background/15 pt-3">
                        <div>
                          <p className="text-background/90">{m.name}</p>
                          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-background/50 mt-1">{m.designation}</p>
                        </div>
                        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary shrink-0 mt-1">{m.role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-8">
            <div className="overflow-hidden border border-border">
              <img src={MEDIA.team} alt="Team Paptech" className="w-full h-[520px] object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-4">
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Team Paptech</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">The people behind the paper.</h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              From the trade desk to the sheet-cutting floor, a hands-on team keeps every order moving — united by one goal: making paper ordering simpler.
            </p>
          </FadeUp>
        </div>
      </section>

      <Footer />
    </div>
  );
}
